import {
  Box,
  Typography,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  IconButton,
  Paper,
  Chip,
} from "@mui/material";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ForumIcon from "@mui/icons-material/Forum";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMemo, useState } from "react";
import { HeaderBox, IconCircle, integrations } from "../utils";
import IntegrationConfigDrawer from "./IntegrationConfigDrawer";
import BasicModal from "@/Components/Common/Modal/BasicModal";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useAppSelector } from "@/app/hooks";
import { useGetConfigDetailsQuery } from "@/features/bughurd/api";
import { LoadingButton } from "@mui/lab";

const integrationTypeMap: Record<string, string> = {
  Jira: "JIRA",
  Asana: "ASANA",
  "Microsoft Teams": "TEAMS",
  Slack: "SLACK",
  "Microsoft Azure": "AZURE",
};

const groupMeta: Record<
  string,
  { icon: React.ReactNode; subtitle: string }
> = {
  "Project Management": {
    icon: <AccountTreeIcon sx={{ fontSize: 16, color: "inherit" }} />,
    subtitle: "Connect your project management tool to sync tasks and issues.",
  },
  "Communication Channel": {
    icon: <ForumIcon sx={{ fontSize: 16, color: "inherit" }} />,
    subtitle:
      "Connect a channel to receive real-time notifications for bugs and feedback.",
  },
};

const Integrations = ({ projectDetails }) => {
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal,
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation(["bugherd"]);
  const [isJiraOpen, setIsJiraOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [isOverviewModal, setIsOverviewModal] = useState(false);

  const { data: configData, isLoading: isLoadingConfig } =
    useGetConfigDetailsQuery({});

  const isIntegrationConnected = (integrationName: string): boolean => {
    if (!projectDetails) return false;

    const integrationType = integrationTypeMap[integrationName];
    if (!integrationType) return false;

    // Check Project Management integrations (Jira, Azure, Asana)
    if (["JIRA", "AZURE", "ASANA"].includes(integrationType)) {
      return projectDetails?.integrationType === integrationType;
    }

    // Check Communication Channel integrations (Slack, Teams)
    if (["SLACK", "TEAMS"].includes(integrationType)) {
      return projectDetails?.communicationChannel === integrationType;
    }

    return false;
  };

  const isGroupConnected = (groupName: string): boolean => {
    const groupItems = integrations.filter((i) => i.group === groupName);
    return groupItems.some((i) => isIntegrationConnected(i.name));
  };

  const groupedIntegrations = useMemo(() => {
    const groups: Record<string, typeof integrations> = {};
    integrations.forEach((integration) => {
      const group = integration.group || "Other";
      if (!groups[group]) groups[group] = [];
      groups[group].push(integration);
    });
    return groups;
  }, []);

  return (
    <Container
      maxWidth={false}
      sx={{
        maxWidth: "900px",
        margin: "0 auto",
        p: 0,
        pb: 3,
        maxHeight: "calc(100vh - 140px)",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.2)",
          borderRadius: "3px",
        },
      }}
    >
      <HeaderBox>
        <Box sx={{ flex: 1, maxWidth: "70%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                background: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(255, 255, 255, 0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(10px)",
              }}
            >
              <ElectricBoltIcon sx={{ fontSize: 22, color: "inherit" }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              fontWeight="600"
              sx={{ fontSize: "28px", lineHeight: 1.2 }}
            >
              {t("Integrations")}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "14.5px",
              lineHeight: 1.5,
              opacity: 0.95,
              maxWidth: "90%",
            }}
          >
            {t(
              "Connect Issue Tracker with your favorite tools. Sync tasks, send notifications, and streamline your workflow across platforms.",
            )}
          </Typography>
        </Box>
      </HeaderBox>

      <Stack spacing={2.5}>
        {Object.entries(groupedIntegrations).map(
          ([groupName, groupIntegrations]) => {
            const meta = groupMeta[groupName];
            return (
              <Paper
                key={groupName}
                elevation={0}
                sx={{
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.03)"
                      : theme.palette.background.paper,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                {/* Group Header */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.8,
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                    background: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.02)"
                        : "rgba(0, 0, 0, 0.015)",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.2}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "7px",
                        background: (theme) =>
                          `${theme.palette.primary.main}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: (theme) => theme.palette.primary.main,
                      }}
                    >
                      {meta?.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          fontSize: "13.5px",
                          color: (theme) => theme.palette.text.primary,
                        }}
                      >
                        {t(groupName)}
                      </Typography>
                      {meta?.subtitle && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "11.5px",
                            color: (theme) => theme.palette.text.secondary,
                            lineHeight: 1.3,
                          }}
                        >
                          {t(meta.subtitle)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Integration Items */}
                {groupIntegrations.map((integration, index) => {
                  const connected = isIntegrationConnected(integration.name);
                  const groupHasConnection = isGroupConnected(groupName);
                  const isLast = index === groupIntegrations.length - 1;

                  return (
                    <Box
                      key={integration.id}
                      sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        alignItems: isMobile ? "flex-start" : "center",
                        gap: 2,
                        px: 2.5,
                        py: 2,
                        borderBottom: (theme) =>
                          isLast
                            ? "none"
                            : `1px solid ${theme.palette.divider}`,
                        transition: "background 0.15s ease",
                        "&:hover": {
                          background: (theme) =>
                            theme.palette.mode === "dark"
                              ? "rgba(255, 255, 255, 0.02)"
                              : "rgba(0, 0, 0, 0.01)",
                        },
                      }}
                    >
                      {/* Icon + Info */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <IconCircle
                          sx={{
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            border: (theme) =>
                              `1px solid ${theme.palette.divider}`,
                            background: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.06)"
                                : "#fff",
                            "& svg": {
                              width: "20px",
                              height: "20px",
                              maxWidth: "20px",
                              maxHeight: "20px",
                            },
                          }}
                        >
                          {integration.icon}
                        </IconCircle>
                        <Box sx={{ minWidth: 0 }}>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                            mb={0.75}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: "13.5px",
                                color: (theme) => theme.palette.text.primary,
                              }}
                            >
                              {t(integration.name)}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "12px",
                              color: (theme) => theme.palette.text.secondary,
                              lineHeight: 1.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {t(integration.description)}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Status Chip + Action Button */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          ml: isMobile ? "auto" : 0,
                          width: isMobile ? "100%" : "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Chip
                          icon={
                            connected ? (
                              <CheckCircleIcon
                                sx={{ fontSize: "14px !important" }}
                              />
                            ) : undefined
                          }
                          label={connected ? t("Connected") : t("Not Connected")}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "11px",
                            fontWeight: 600,
                            ...(connected
                              ? {
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(46, 125, 50, 0.15)"
                                      : "#e8f5e9",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "#66bb6a"
                                      : "#2e7d32",
                                  border: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "1px solid rgba(102, 187, 106, 0.3)"
                                      : "1px solid #c8e6c9",
                                  "& .MuiChip-icon": {
                                    ml: "4px",
                                    color: (theme) =>
                                      theme.palette.mode === "dark"
                                        ? "#66bb6a"
                                        : "#2e7d32",
                                  },
                                }
                              : {
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.08)"
                                      : "#f5f5f5",
                                  color: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "rgba(255, 255, 255, 0.5)"
                                      : "#9e9e9e",
                                  border: (theme) =>
                                    theme.palette.mode === "dark"
                                      ? "1px solid rgba(255, 255, 255, 0.15)"
                                      : "1px solid #e0e0e0",
                                }),
                          }}
                        />
                        <LoadingButton
                          loading={isLoadingConfig}
                          onClick={() => {
                            setSelectedPlatform(
                              integrationTypeMap[integration.name] || "JIRA",
                            );
                            setIsJiraOpen(true);
                          }}
                          variant="outlined"
                          size="small"
                          startIcon={<ElectricBoltIcon sx={{ fontSize: 16 }} />}
                          sx={{
                            borderRadius: "7px",
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            fontSize: "12.5px",
                            fontWeight: 500,
                            px: 2,
                            height: 32,
                            width: isMobile ? "100%" : "auto",
                            boxShadow: "none",
                            "&:hover": { boxShadow: "none" },
                          }}
                          disabled={connected || !DEFEKT_TRACKER.CONNECT(myPermissions)}
                        >
                          {t("Configure")}
                        </LoadingButton>
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            );
          },
        )}
      </Stack>

      <IntegrationConfigDrawer
        open={isJiraOpen}
        onClose={() => setIsJiraOpen(false)}
        projectDetails={projectDetails}
        platform={selectedPlatform}
      />

      <BasicModal
        open={isOverviewModal}
        onClose={() => setIsOverviewModal(false)}
      >
        <Box px={3} py={2} position="relative">
          <IconButton
            size="small"
            onClick={() => setIsOverviewModal(false)}
            sx={{
              position: "absolute",
              top: -16,
              right: -17,
              background: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#000",
              color: (theme) =>
                theme.palette.mode === "light" ? "#fff" : "#000",
            }}
          >
            <Close />
          </IconButton>
          <iframe
            width="100%"
            height="450"
            src="https://www.youtube.com/embed/xR0dgx2Ofo0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            style={{
              borderRadius: "10px",
              border: "none",
            }}
          ></iframe>
        </Box>
      </BasicModal>
    </Container>
  );
};

export default Integrations;
